import { type ReactNode } from 'react';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Box, { type BoxProps } from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { Link, useLocation } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import useTheme from '@mui/styles/useTheme';
import { NotificationBell } from '@mercedes-benz/backstage-plugin-notification-bell';
import { NavBackButton } from '../NavBackButton';

type Props = {
  readonly children: ReactNode;
  readonly rootPath?: string | 'root';
  readonly toolbarActions?: ReactNode;
  readonly toolbarBreadcrumbs?: ReactNode;
  readonly mainSx?: BoxProps['sx'];
  readonly onNavigateBack?: () => void;
};

const rootPathToFallback = (rootPath?: string | 'root') =>
  rootPath && rootPath !== 'root' ? rootPath : '/';

export function AppShell({
  children,
  rootPath,
  toolbarActions,
  toolbarBreadcrumbs,
  mainSx,
  onNavigateBack,
}: Props) {
  const location = useLocation();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const fallbackPath = rootPathToFallback(rootPath);
  const isAtRootPath = location.pathname === fallbackPath;
  const isSettingsPage =
    location.pathname.startsWith('/settings') ||
    location.pathname.startsWith('/user-settings');

  return (
    <Box
      sx={{
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
      }}
    >
      <AppBar
        position="sticky"
        elevation={0}
        color="transparent"
        sx={{
          marginBottom: 0,
          backdropFilter: 'blur(14px)',
          backgroundColor: isDark ? 'rgba(11,13,16,0.6)' : '#ffffff',
          borderBottom: `1px solid`,
          borderBottomColor: `${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(15,20,30,0.06)'}`,
        }}
      >
        <Toolbar sx={{ minHeight: 64, px: { xs: 2, md: 4 } }}>
          <NavBackButton
            onClick={isAtRootPath ? undefined : onNavigateBack}
            iconOnly={Boolean(toolbarBreadcrumbs)}
            fallbackPath={fallbackPath}
            parentPath={fallbackPath !== '/' ? fallbackPath : undefined}
            sx={{ textTransform: 'none', color: 'text.primary' }}
          />
          {toolbarBreadcrumbs && (
            <Box
              sx={{
                minWidth: 0,
                ml: 2,
                pl: 2,
                borderLeft: '1px solid',
                borderColor: 'divider',
              }}
            >
              {toolbarBreadcrumbs}
            </Box>
          )}
          <Box sx={{ flex: 1 }} />
          {toolbarActions}
          <NotificationBell />
          {!isSettingsPage && (
            <Tooltip title="User profile">
              <IconButton
                aria-label="Go to User Area"
                color="inherit"
                component={Link}
                data-testid="navbar-settings-button"
                to="/settings"
              >
                <AccountCircleIcon />
              </IconButton>
            </Tooltip>
          )}
        </Toolbar>
      </AppBar>
      <Box
        component="main"
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
          ...mainSx,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}
